import { AppDispatch } from '@/app/store';
import { AttachFileTableDTO } from '@/types/attachment/AttachFileTableDTO';
import { AttachFileViewDTO } from '@/types/attachment/AttachFileViewDTO';
import { AttachmentStatus } from '@/types/attachment/AttachmentStatus';
import { Box, Grid, MenuItem, Pagination, SelectChangeEvent, Typography, useMediaQuery } from '@mui/material';
import { ErrorDTO } from '@/types/ErrorDTO';
import { ImageView, ImageViewItem, useImageView } from '@/app/hooks/useImageView';
import { ImageViewDialog } from '@/components/view/image/ImageViewDialog';
import { LetterRequestDTO } from '@/types/letter/LetterRequestDTO';
import { LetterSimpleStatus } from '@/types/letter/LetterSimpleStatus';
import { LetterTableDTO } from '@/types/letter/LetterTableDTO';
import { NavigateFunction, useNavigate, useOutletContext } from 'react-router-dom';
import { OutboxList } from '@/components/home/outbox/OutboxList';
import { OutboxTable } from '@/components/home/outbox/OutboxTable';
import { OutlinedField } from '@/components/common/field/OutlinedField';
import { OutlinedSelect } from '@/components/common/field/OutlinedSelect';
import { SortRequestDTO } from '@/types/page/SortRequestDTO';
import { Theme } from '@mui/material/styles';
import { UserDTO } from '@/types/user/UserDTO';
import { VerdictDot } from '@/components/letter/VerdictDot';
import { formatPhotoLabel } from '@/utils/string-utils';
import { getCurrentUser } from '@/services/authSlice';
import {
  getLetterOutboxRequest,
  setLetterOutboxRequestPageNumber,
  setLetterOutboxRequestPageSize,
  setLetterOutboxSearchRequestAddressee,
  setLetterOutboxSearchRequestStatus,
  setLetterOutboxSortRequest,
} from '@/services/letter/letterOutboxSlice';
import { getOutboxLetterRoute } from '@/app/routes';
import { getStatusLabel } from '@/utils/status-utils';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useDebounce } from '@/app/hooks/useDebounce';
import { useGetLettersQuery, useSetLetterReadMutation } from '@/services/api/letterApiSlice';
import { useGetPhotoMutation } from '@/services/api/attachFileApiSlice';
import { useSnackbar } from 'notistack';
import React, { ChangeEvent, FC, Fragment, ReactElement, useEffect, useState } from 'react';

interface OutletProps {
  isSearchFieldEnable?: boolean;
}

export const OutboxBlock: FC = (): ReactElement => {
  const { isSearchFieldEnable } = useOutletContext<OutletProps>();

  const dispatch: AppDispatch = useAppDispatch();
  const navigate: NavigateFunction = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const request: LetterRequestDTO = useAppSelector(getLetterOutboxRequest);
  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);

  const { data, isFetching, isLoading } = useGetLettersQuery({ ...request, userId: authUser?.id }, { skip: !authUser });

  const [getPhoto, { isLoading: isAttachFileLoading }] = useGetPhotoMutation();
  const [readLetter] = useSetLetterReadMutation();

  const [search, setSearch] = useState<string>('');
  const [photoId, setPhotoId] = useState<number | undefined>(undefined);
  const [letterNumber, setLetterNumber] = useState<number | undefined>(undefined);

  const imageView: ImageView = useImageView();

  const isMobile: boolean = useMediaQuery((theme: Theme): string => theme.breakpoints.down('sm'));

  const handlePage = (page: number): void => {
    dispatch(setLetterOutboxRequestPageNumber(page - 1));
  };

  const handleAddressee = useDebounce<string>((addressee: string): void => {
    dispatch(setLetterOutboxSearchRequestAddressee(addressee));
    dispatch(setLetterOutboxRequestPageNumber(0));
  }, 500);

  const handleSearch = (addressee: string): void => {
    setSearch(addressee);
    handleAddressee(addressee);
  };

  const handleStatus = (status: LetterSimpleStatus): void => {
    dispatch(setLetterOutboxSearchRequestStatus(status));
    dispatch(setLetterOutboxRequestPageNumber(0));
  };

  const handleSort = (sortRequest: SortRequestDTO): void => {
    dispatch(setLetterOutboxSortRequest(sortRequest));
  };

  const handleLetterClick = async (letter: LetterTableDTO): Promise<void> => {
    navigate(getOutboxLetterRoute(letter.id));
    if (letter.unread) {
      await readLetter(letter.id)
        .unwrap()
        .catch((e: { status: number; data: ErrorDTO }): void => {
          enqueueSnackbar(e.data.message, { variant: 'error' });
        });
    }
  };

  /**
   * Opens one photo of a row full screen. A letter carries up to ten of them, so the row addresses
   * the photo it was clicked on and its original is read one photo at a time.
   *
   * @param letter The letter of the row.
   * @param attachFile The photo the button belongs to.
   */
  const handleAttachClick = async (letter: LetterTableDTO, attachFile: AttachFileTableDTO): Promise<void> => {
    setPhotoId(attachFile.id);
    setLetterNumber(letter.number);
    const photos: AttachFileTableDTO[] = letter.attachFiles || [];
    await imageView.openList(
      photos.map(
        (it: AttachFileTableDTO): ImageViewItem => ({
          name: formatPhotoLabel(it.photoNumber, it.photoIndex, photos.length),
          declined: it.status === AttachmentStatus.DECLINED,
          read: async (): Promise<string | undefined> => {
            setPhotoId(it.id);
            const photo: AttachFileViewDTO = await getPhoto(it.id).unwrap();
            return photo?.content;
          },
        }),
      ),
      photos.indexOf(attachFile),
    );
  };

  useEffect((): void => {
    dispatch(setLetterOutboxRequestPageSize(isMobile ? 5 : 8));
  }, [dispatch, isMobile]);

  useEffect((): void => {
    setSearch(request.searchRequest.addressee || '');
  }, [request.searchRequest.addressee]);

  return (
    <Fragment>
      <Grid container={true} direction={'column'} spacing={2}>
        <Grid item={true}>
          <Grid container={true} direction={'row'} spacing={1}>
            {isSearchFieldEnable && (
              <Grid item={true} xs={true}>
                <OutlinedField
                  fullWidth={true}
                  size={'small'}
                  placeholder={'Поиск по адресату'}
                  name={'addressee'}
                  value={search}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                    handleSearch(e.target.value);
                  }}
                  onClear={(): void => {
                    handleSearch('');
                  }}
                />
              </Grid>
            )}
            <Grid
              item={true}
              xs={isSearchFieldEnable ? 5 : 12}
              md={isSearchFieldEnable ? 4 : 12}
              sm={isSearchFieldEnable ? 3 : 12}>
              <OutlinedSelect
                size={'small'}
                displayEmpty={true}
                fullWidth={true}
                name={'status'}
                value={request.searchRequest.status}
                onChange={(e: SelectChangeEvent<unknown>): void => {
                  handleStatus(e.target.value as LetterSimpleStatus);
                }}>
                {Object.values(LetterSimpleStatus).map((value: string) => (
                  <MenuItem key={value} value={value}>
                    {getStatusLabel(value as LetterSimpleStatus)}
                  </MenuItem>
                ))}
              </OutlinedSelect>
            </Grid>
          </Grid>
        </Grid>
        <Grid item={true}>
          {isMobile ? (
            <OutboxList
              isFetching={isFetching}
              isLoading={isLoading}
              isAttachFileLoading={isAttachFileLoading}
              photoId={photoId}
              letters={data?.content || []}
              onLetterClick={handleLetterClick}
              onAttachClick={handleAttachClick}
            />
          ) : (
            <OutboxTable
              isFetching={isFetching}
              isLoading={isLoading}
              isAttachFileLoading={isAttachFileLoading}
              photoId={photoId}
              letters={data?.content || []}
              sortRequest={
                request.pageRequest.sortRequest || {
                  properties: ['id'],
                  direction: 'DESC',
                }
              }
              onSort={handleSort}
              onLetterClick={handleLetterClick}
              onAttachClick={handleAttachClick}
            />
          )}
        </Grid>
        <Grid item={true} display={'flex'} justifyContent={isMobile ? 'center' : 'right'}>
          <Pagination
            count={data?.totalPages || 1}
            page={(request.pageRequest?.pageNumber || 0) + 1}
            onChange={(_: ChangeEvent<unknown>, page: number): void => {
              handlePage(page);
            }}
          />
        </Grid>
      </Grid>
      <ImageViewDialog
        open={imageView.isOpen}
        isLoading={imageView.isLoading}
        isFailed={imageView.isFailed}
        content={imageView.content || ''}
        onPreviousImage={imageView.hasPrevious ? imageView.previous : undefined}
        onNextImage={imageView.hasNext ? imageView.next : undefined}
        title={
          <Grid container={true} direction={'row'} alignItems={'center'} spacing={2}>
            <Grid item={true}>
              <Typography variant={'h1'} fontSize={'24px'} lineHeight={'20px'}>
                {`Вложение письма № ${letterNumber}`}
              </Typography>
            </Grid>
            <Grid item={true}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {imageView.declined !== undefined && <VerdictDot declined={imageView.declined} />}
                <Typography
                  variant={'label2'}
                  fontSize={'12px'}
                  lineHeight={'20px'}
                  sx={{ color: (theme: Theme) => theme.colors.grayText }}>
                  {imageView.name}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        }
        onClose={imageView.close}
      />
    </Fragment>
  );
};
